import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mx from "./mx.json";
import en from "./en.json";

const DICTS = { mx, en };

const I18nContext = createContext({
  lang: "mx",
  t: (k) => k,
  switchLang: () => {},
  buildPath: () => "/",
});

const ROUTE_MAP = {
  privacy: { mx: "privacidad", en: "privacy" },
  cookies: { mx: "cookies", en: "cookies" },
  terms:   { mx: "terminos",  en: "terms" },
};

function detectLangFromPath(pathname) {
  if (pathname.startsWith("/en")) return "en";
  return "mx";
}

function get(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    const idxMatch = key.match(/^(\w+)\[(\d+)\]$/);
    if (idxMatch) {
      const [, k, i] = idxMatch;
      return acc[k] ? acc[k][Number(i)] : undefined;
    }
    return acc[key];
  }, obj);
}

export const I18nProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);

  const value = useMemo(() => {
    const dict = DICTS[lang] || DICTS.mx;

    const t = (key, fallback) => {
      const v = get(dict, key);
      if (v === undefined || v === null) return fallback ?? key;
      return v;
    };

    const tArr = (key) => {
      const v = get(dict, key);
      return Array.isArray(v) ? v : [];
    };

    const buildPath = (routeKey) => {
      if (routeKey === "home") return `/${lang}`;
      const slug = ROUTE_MAP[routeKey]?.[lang];
      return slug ? `/${lang}/${slug}` : `/${lang}`;
    };

    const switchLang = () => {
      const target = lang === "mx" ? "en" : "mx";
      // Try to preserve current page
      const parts = location.pathname.split("/").filter(Boolean);
      const rest = parts.slice(1);
      // Map legal slugs
      let mapped = rest;
      if (rest.length > 0) {
        const slug = rest[0];
        const entry = Object.entries(ROUTE_MAP).find(([, m]) => m[lang] === slug);
        if (entry) {
          const [routeKey] = entry;
          mapped = [ROUTE_MAP[routeKey][target]];
        }
      }
      const newPath = `/${target}${mapped.length ? "/" + mapped.join("/") : ""}`;
      navigate(newPath + location.hash, { replace: false, state: { langSwitch: true } });
    };

    return { lang, t, tArr, buildPath, switchLang };
  }, [lang, location.pathname, location.hash, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
